import React, { useMemo, useRef } from 'react';
import { ActionPopup, List, Text, AssetPreview } from '@shootsta/common-react';
import type { RouterHistory } from '@core/shared';
import { PRODUCT_TYPE } from '@shootsta/common-domain-data';

import STATUS_COLORS_MAP from '../../constants/videoStatusColorMap';
import ThumbnailAssetSelector from '../../../ThumbnailAssetSelector';

import getMasterOutput from '../../utils/getMasterOutput';
import getBasePathByProductType from '../../utils/getBasePathByProductType';
import VideoId from '../../../VideoId';
import elevatePath from '../../constants/elevatePath';
import elevateWorkFlowV2Path from '../../constants/elevateV2Path';

import getStatusForVideo from '../../utils/getStatusForVideo';

import './styles/_project-list-item.scss';

type ProjectListItemProps = {
  isShowArchived: boolean;
  onToggleArchived: any;
  video: Video;
  onDelete: any;
  onDuplicate: any;
  onShare: any;
  onRename: any;
  onProjectCostSummary: any;
  markProjectAsRead: any;
  onPublicUpload: any;
  history: RouterHistory;
  product?: string;
  globalStore: any;
  flags?: any;
};

const VIDEO_STATUSES = {
  DRAFT: { name: 'DRAFT', label: 'Draft' },
  AWAITING_ACTION: { name: 'AWAITING_ACTION', label: 'Requires action' },
  ON_HOLD: { name: 'ON_HOLD', label: 'On hold' },
  COMPLETE: { name: 'COMPLETE', label: 'Complete' },
  IN_PROGRESS: { name: 'IN_PROGRESS', label: 'In-progress' },
  SIGN_OFF_FINAL: { name: 'SIGN_OFF_FINAL', label: 'Pending Sign-off' }
} as const;

function withNoEventPropagation(callback: any) {
  return (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    callback(e);
  };
}

const onItemClick = (
  video: Video,
  history: any,
  actionPopupRef: {
    current: undefined;
  },
  product: undefined | string,
  globalStore: any,
  flags: any,
  status: any,
  markProjectAsRead: any
) =>
  withNoEventPropagation((e) => {
    if (
      actionPopupRef &&
      actionPopupRef.current &&
      actionPopupRef.current.contains(e.target)
    ) {
      return;
    }

    const { getObjectForKey, updateObjectForKey } = globalStore;

    const basePath = getBasePathByProductType(product);

    const { serviceType } = video;

    const { elevateWorkFlowV2 = false } = flags;
    let newPath = `${basePath}/project/${video.videoId}/`;

    if (
      elevatePath === basePath &&
      serviceType &&
      elevateWorkFlowV2 &&
      status === VIDEO_STATUSES.DRAFT
    ) {
      newPath = `${basePath}${`${elevateWorkFlowV2Path}/${video.videoId}/`}`;
    }

    // Pre-populate ProjectDetail state for instant display
    if (!getObjectForKey(newPath).video) {
      updateObjectForKey(newPath, {
        video,
        project: {},
        loading: false
      });
    }

    history.rootPush(newPath);
    markProjectAsRead(video);
  });

const useArchivedOptions = ({
  isShowArchived,
  video,
  onToggleArchived,
  onDelete
}) =>
  useMemo(
    () =>
      isShowArchived
        ? [
            {
              action: withNoEventPropagation(onToggleArchived),
              label: 'Unarchive'
            },
            {
              action: withNoEventPropagation(onDelete),
              label: 'Delete',
              isRed: true
            }
          ]
        : [
            {
              action: withNoEventPropagation(onToggleArchived),
              label: 'Archive',
              isRed: true
            }
          ],
    [isShowArchived, video, onToggleArchived, onDelete]
  );

const useActionPopupOptions = ({
  video,
  history,
  isShowArchived,
  onShare,
  onRename,
  onDuplicate,
  onProjectCostSummary,
  onPublicUpload,
  onToggleArchived,
  onDelete,
  product
}) => {
  const { name } = video;

  const archivedOptions = useArchivedOptions({
    isShowArchived,
    video,
    onToggleArchived,
    onDelete
  });

  const status = getStatusForVideo(video, VIDEO_STATUSES);

  const actions = [
    ...(product !== PRODUCT_TYPE.PITCHTEMPLATE
      ? [
          {
            action: withNoEventPropagation(onShare),
            label: 'Share With Others'
          }
        ]
      : []),
    { action: withNoEventPropagation(onRename), label: 'Rename' },
    { action: withNoEventPropagation(onDuplicate), label: 'Duplicate' },
    {
      action: withNoEventPropagation(onProjectCostSummary),
      label: 'Project cost summary'
    },
    { separator: true }
  ];

  if (status.name !== VIDEO_STATUSES.COMPLETE.name) {
    actions.push({
      action: withNoEventPropagation(onPublicUpload),
      label: 'Team Uploader'
    });
  }

  actions.push(...archivedOptions);

  return useMemo(
    () => actions,
    [
      video,
      history,
      name,
      onShare,
      onRename,
      onDuplicate,
      archivedOptions,
      onProjectCostSummary,
      onPublicUpload
    ]
  );
};

export default function ProjectListItem({
  history,
  video,
  onDelete,
  onShare,
  isShowArchived,
  onToggleArchived,
  onDuplicate,
  onRename,
  onPublicUpload,
  onProjectCostSummary,
  markProjectAsRead,
  product,
  globalStore,
  flags = {}
}: ProjectListItemProps) {
  const { outputs, clips, readableId } = video;
  const { name: videoName } = video;
  const masterOutput = getMasterOutput(video);
  const actionPopupRef = useRef();
  const status = getStatusForVideo(video, VIDEO_STATUSES);
  const onClick = onItemClick(
    video,
    history,
    actionPopupRef,
    product,
    globalStore,
    flags,
    status,
    markProjectAsRead
  );
  const statusColor = STATUS_COLORS_MAP[status.name];
  const actionPopupOptions = useActionPopupOptions({
    video,
    history,
    videoName,
    isShowArchived,
    onShare,
    onRename,
    onDuplicate,
    onToggleArchived,
    onDelete,
    onProjectCostSummary,
    onPublicUpload,
    product
  });

  if (!masterOutput) {
    return null;
  }

  const { versions, latestVersion } = masterOutput;
  const { assetId } =
    versions.find(({ versionNumber }) => versionNumber === latestVersion) || {};

  const itemName =
    product && product === PRODUCT_TYPE.PITCHTEMPLATE ? 'Template' : 'Video';

  return (
    <ThumbnailAssetSelector
      assetId={assetId}
      videoAssetIds={clips.map((clip) => clip.assetId).filter(Boolean)}
    >
      {(thumbnailAsset) => (
        <List.CardItem
          onClick={onClick}
          customCoverImage={
            thumbnailAsset && (
              <AssetPreview
                asset={thumbnailAsset}
                hideFileTypeIcon
                canPlayVideo={false}
                darkMode
              />
            )
          }
          coverImageProps={{
            defaultImageIcon: 'video',
            defaultIconColour: 'russian',
            defaultIconSize: 50
          }}
        >
          <div
            className="project-list-item__preview__status"
            style={{ background: statusColor }}
          >
            <Text darkTheme noMargin centered>
              {status.label}
            </Text>
          </div>
          <VideoId videoId={readableId ?? 'N/A'} />
          <div className="project-list-item__body">
            <div className="project-list-item__body__description">
              <div
                className="project-list-item__body__description__name"
                title={video.name}
              >
                <Text heading4 noMargin>
                  {video.name}
                </Text>
              </div>
              <div className="project-list-item__body__description__count">
                <Text
                  label
                >{`${outputs.length} ${itemName}${outputs.length !== 1 ? 's' : ''}`}</Text>
              </div>
            </div>
            <ActionPopup
              ref={actionPopupRef.current}
              options={actionPopupOptions}
            />
          </div>
        </List.CardItem>
      )}
    </ThumbnailAssetSelector>
  );
}
